import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { useDocumentViewer } from '../contexts/DocumentViewerContext'
import { certDisplayLabel, profileService } from '../services/profileService'
import {
  resourcesService,
  type ResourceContactCert,
} from '../services/resourcesService'

type ContactArticle = {
  type: 'Contact'
  employeeId?: string
  name: string
  email: string
  phone?: string
  role?: string
  organization?: string
  notes?: string
}

interface ContactArticleModalProps {
  open: boolean
  onClose: () => void
  contact?: ContactArticle
}

function certDisplayName(cert: { type?: string; label?: string | null }): string {
  return certDisplayLabel(cert) || '—'
}

function certStatusLabel(status: string): string {
  if (status === 'good') return 'Complete'
  if (status === 'bad') return 'Expired'
  if (status === 'inProgress') return 'In Progress'
  return status.trim() || '—'
}

function certStatusBadgeClass(status: string): string {
  if (status === 'good') return 'badge-success'
  if (status === 'bad') return 'badge-error'
  if (status === 'inProgress') return 'badge-warning'
  return 'badge-ghost'
}

function formatCertExpiration(value: unknown): string {
  if (value == null || value === '') return '—'
  const raw = String(value).trim()
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (dateOnly) return `${dateOnly[2]}/${dateOnly[3]}/${dateOnly[1]}`
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return raw
  return parsed.toLocaleDateString()
}

const ContactArticleModal: React.FC<ContactArticleModalProps> = ({ open, onClose, contact }) => {
  const employeeId = String(contact?.employeeId || '').trim()
  const [certs, setCerts] = useState<ResourceContactCert[]>([])
  const [certsLoading, setCertsLoading] = useState(false)
  const [certsError, setCertsError] = useState<string | null>(null)
  const [openingCertType, setOpeningCertType] = useState<string | null>(null)
  const { openDocument } = useDocumentViewer()

  useEffect(() => {
    if (!open || !employeeId) {
      setCerts([])
      setCertsError(null)
      setCertsLoading(false)
      return undefined
    }

    let cancelled = false
    setCertsLoading(true)
    setCertsError(null)
    resourcesService
      .listContactCerts(employeeId)
      .then((rows) => {
        if (!cancelled) setCerts(rows)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setCerts([])
        setCertsError(err instanceof Error ? err.message : 'Failed to load certifications')
      })
      .finally(() => {
        if (!cancelled) setCertsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, employeeId])

  const openCertFile = async (cert: ResourceContactCert) => {
    const certType = String(cert.type || '').trim()
    if (!employeeId || !certType || !cert.hasFile) return
    const title = certDisplayName(cert)
    setOpeningCertType(certType)
    openDocument(title, 'PDF')
    try {
      const blobUrl = await profileService.getCertFileBlobUrl(employeeId, certType)
      openDocument(title, 'PDF', blobUrl)
    } catch {
      openDocument(title, 'PDF', null, 'Failed to load certificate document.')
    } finally {
      setOpeningCertType((current) => (current === certType ? null : current))
    }
  }

  if (!open || !contact) return null

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[1990] flex items-center justify-center bg-black/40 p-4"
      onClick={handleOverlayClick}
    >
      <div
        className="relative flex w-full max-w-xl flex-col overflow-hidden rounded-lg bg-base-100 p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex shrink-0 items-start gap-3 border-0 p-0 pr-10">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold sm:text-2xl">{contact.name}</h2>
            {contact.role ? <div className="mt-1 text-base-content/70">{contact.role}</div> : null}
            {contact.organization ? (
              <div className="text-base-content/70">{contact.organization}</div>
            ) : null}
          </div>
          <button
            type="button"
            className="btn btn-sm btn-circle btn-ghost shrink-0 absolute right-2 top-2"
            aria-label="Close contact"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className={clsx('min-h-0 flex-1 overflow-y-auto overscroll-y-contain', 'mt-2')}>
          <div className="mb-2">
            <span className="font-semibold">Email:</span>{' '}
            <a href={`mailto:${contact.email}`} className="link">
              {contact.email}
            </a>
          </div>
          {contact.phone ? (
            <div className="mb-2">
              <span className="font-semibold">Phone:</span>{' '}
              <a href={`tel:${contact.phone}`} className="link">
                {contact.phone}
              </a>
            </div>
          ) : null}
          {contact.notes ? <div className="mt-4 text-base-content/80">{contact.notes}</div> : null}

          <div className="mt-5">
            <h3 className="mb-2 font-semibold">Certifications</h3>
            {certsLoading ? (
              <div className="flex items-center gap-2 py-3 text-sm text-base-content/60">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                Loading…
              </div>
            ) : certsError ? (
              <p className="text-sm text-error">{certsError}</p>
            ) : certs.length === 0 ? (
              <p className="text-sm text-base-content/60">No certifications assigned</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Certification</th>
                      <th>Status</th>
                      <th>Expiration</th>
                      <th>File</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certs.map((cert) => (
                      <tr key={cert.type}>
                        <td className="font-medium">{certDisplayName(cert)}</td>
                        <td>
                          <span className={`badge badge-sm ${certStatusBadgeClass(cert.status)}`}>
                            {certStatusLabel(cert.status)}
                          </span>
                        </td>
                        <td>{formatCertExpiration(cert.expirationDate)}</td>
                        <td>
                          {cert.hasFile ? (
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs"
                              disabled={openingCertType === cert.type}
                              onClick={() => {
                                void openCertFile(cert)
                              }}
                            >
                              {openingCertType === cert.type ? (
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                              ) : (
                                'View PDF'
                              )}
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContactArticleModal
